import { useEffect, useState } from 'react';

/**
 * A value that lags behind, so typing does not become a request per keystroke.
 *
 * ── WHY THIS EXISTS NOW ──
 * The searches that were free before were free because they filtered rows the
 * browser already had. Moving them to the server is what makes them correct —
 * a filter over one loaded page reports "nothing matches" for a log it never
 * looked at — and it is also what makes them cost a query. Debouncing is the
 * other half of that move, not a nicety: without it, "voided" is seven queries
 * over the whole audit table and six of them are for prefixes nobody wanted.
 *
 * The timer is cleared on every change, so only a genuine pause in typing lets
 * a value through, and unmounting mid-type schedules nothing.
 */
export function useDebounced<T>(value: T, delayMs = 300): T {
  const [settled, setSettled] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => setSettled(value), delayMs);
    return () => clearTimeout(timer);
  }, [value, delayMs]);

  return settled;
}
